import React, { useCallback, useEffect, useState } from 'react';

import { jDatasourceExcel } from 'jamespot-user-api';

import { useIntl } from 'react-intl';
import { EditorSelect } from 'components/Editors/components/EditorSelect';

import { JRCButton } from 'components/JRCButton/JRCButton';

import { Typography } from 'components/JRCTypography/JRCTypography.styles';

import { SUPPORTED_DISPLAY_TYPE } from '../../const';
import { useEditorContext } from 'components/Widgets/context/hooks';
import { ExcelEditorObject } from './ExcelEditorObject';
import { WidgetExcelDatasourceContext } from '../../JRCWidgetExcelDatasourceTableEditor';

export const ExcelEditorSpreadSheets = ({ uri, onHandleCancel }: { uri: string; onHandleCancel: () => void }) => {
    const intl = useIntl();
    const [spreadsheet, setSpreadsheet] = useState<string | undefined>(undefined);
    const [datasource, setDatasource] = useState<jDatasourceExcel | undefined>(undefined);
    const { onChange, handlers } = useEditorContext<WidgetExcelDatasourceContext>();

    const handleCreateModel = useCallback(() => {
        if (uri && spreadsheet) {
            handlers.handleStart();
            handlers.jamespot.datasource
                .setExcelDatasource(uri, spreadsheet)
                .then((response) => {
                    onChange({
                        tableColumnsData: response.result.map((el) => {
                            return { name: el.name, isVisible: SUPPORTED_DISPLAY_TYPE.includes(el.type) };
                        }),
                        uri,
                    });
                })
                .catch((err) => {
                    handlers.handleCatch(err);
                    onHandleCancel();
                })
                .finally(handlers.handleFinally);
        }
    }, [uri, spreadsheet, onChange, handlers, onHandleCancel]);

    useEffect(() => {
        if (!datasource) {
            handlers.handleStart();
            handlers.jamespot.datasource
                .getExcelDatasource(uri)
                .then((response) => setDatasource(response.result))
                .catch((err) => {
                    handlers.handleCatch(err);
                    onHandleCancel();
                })
                .finally(handlers.handleFinally);
        }
    }, [uri, datasource, handlers, onHandleCancel]);

    return datasource && uri ? (
        <>
            <ExcelEditorObject uri={uri} onHandleCancel={onHandleCancel} />
            <EditorSelect
                label={'WIDGET_Datasource_Spreadsheets'}
                options={[
                    {
                        label: intl.formatMessage({ id: 'WIDGET_Datasource_Select_Spreadsheet' }),
                        value: 'select-spreadsheet',
                    },
                    ...datasource.spreadsheetList.map((el) => {
                        return { label: el, value: el };
                    }),
                ]}
                required={true}
                initialValue={'select-spreadsheet'}
                onChange={setSpreadsheet}
            />
            <Typography size={'s'}>
                {intl.formatMessage({ id: 'WIDGET_Datasource_Create_Spreadsheet_Model_Help' })}
            </Typography>
            {spreadsheet && !isValidSpreadsheet(spreadsheet, datasource.spreadsheetListHeaders) && (
                <Typography size={'s'} color="orange">
                    {intl.formatMessage({ id: 'WIDGET_Datasource_Spreadsheet_Header_Error' })}
                </Typography>
            )}
            <JRCButton
                onClick={handleCreateModel}
                color={'primary'}
                minWidth={'100%'}
                disabled={
                    spreadsheet === undefined ||
                    spreadsheet === 'select-spreadsheet' ||
                    !isValidSpreadsheet(spreadsheet, datasource.spreadsheetListHeaders)
                }
                noMargin>
                {intl.formatMessage({ id: 'WIDGET_Datasource_Create_Spreadsheet_Model' })}
            </JRCButton>
            <div style={{ flex: 1 }}></div>
            <JRCButton onClick={onHandleCancel} color={'cancel'} minWidth={'100%'} noMargin>
                {intl.formatMessage({ id: 'WIDGET_Datasource_Cancel' })}
            </JRCButton>
        </>
    ) : (
        <></>
    );
};

export function isValidSpreadsheet(spreadsheet: string, spreadsheetListHeader: Record<string, string[]>) {
    const headers = spreadsheetListHeader[spreadsheet] ?? null;
    if (!headers) {
        return false;
    }

    return !headers.filter((el) => !el || el.length === 0).length;
}
